import React, { useState } from "react";
import { TextField, Box, Autocomplete, CircularProgress, Alert, Snackbar } from "@mui/material";
import { LocationOn } from "@mui/icons-material";
import { LoadScriptNext } from "@react-google-maps/api";
import ErrorBoundary from "./ErrorBoundary";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";

// Must be a stable module-level reference. Passing a fresh array literal to
// LoadScriptNext on every render triggers "LoadScript has been reloaded
// unintentionally" and reloads the Maps script repeatedly.
const GOOGLE_MAPS_LIBRARIES = ["places"];

const getPlaceDetails = (placeId) =>
  new Promise((resolve, reject) => {
    try {
      if (
        !placeId ||
        !window.google ||
        !window.google.maps ||
        !window.google.maps.places
      ) {
        resolve(null);
        return;
      }
      const service = new window.google.maps.places.PlacesService(
        document.createElement("div")
      );
      service.getDetails(
        {
          placeId,
          fields: ["address_components", "geometry", "formatted_address", "name"],
        },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            resolve(place);
          } else {
            reject(new Error(`PlacesService.getDetails failed: ${status}`));
          }
        }
      );
    } catch (e) {
      reject(e);
    }
  });

const AddressAutocompleteInner = ({
  onAddressSelect,
  placeholder = "Search for an address",
  countryRestriction = null,
  value = "",
  onChange,
  ...props
}) => {
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState(null);

  const {
    ready,
    value: searchValue,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: countryRestriction
      ? {
          componentRestrictions: { country: countryRestriction },
        }
      : {},
    debounce: 300,
    initOnMount: true,
  });

  const handleSelect = async (place) => {
    if (!place) return;

    setLoading(true);
    setValue(place.description, false);
    clearSuggestions();

    try {
      let results;
      let addressComponents = [];
      let lat = null;
      let lng = null;

      try {
        // Prefer place_id based geocode (more reliable for postal_code/pincode)
        const placeId = place.place_id || place.placeId || null;
        // 1) Try Place Details first (best for postal_code)
        if (placeId) {
          try {
            const details = await getPlaceDetails(placeId);
            if (details?.address_components?.length) {
              addressComponents = details.address_components || [];
              if (details.geometry?.location) {
                lat = details.geometry.location.lat();
                lng = details.geometry.location.lng();
              }
              // Also keep formatted address if present
              results = [
                {
                  formatted_address: details.formatted_address,
                  address_components: details.address_components,
                  geometry: details.geometry,
                },
              ];
            }
          } catch (detailsErr) {
            console.warn("Place Details failed, falling back to geocode:", detailsErr);
          }
        }

        // 2) Fallback to Geocoding API if we still don't have components
        if (!addressComponents.length) {
          results = placeId
            ? await getGeocode({ placeId })
            : await getGeocode({ address: place.description });
        }
        if (results && results[0]) {
          try {
            // Only compute lat/lng from geocode if we don't already have it from place details
            if (lat == null || lng == null) {
              const coords = await getLatLng(results[0]);
              lat = coords.lat;
              lng = coords.lng;
            }
          } catch (latLngError) {
            console.warn("getLatLng failed, continuing without coordinates:", latLngError);
          }
          if (!addressComponents.length) {
            addressComponents = results[0].address_components || [];
          }
        }
      } catch (geocodeError) {
        console.warn("Geocoding failed, using fallback parsing:", geocodeError);
        // If geocoding fails (e.g., API key issue), we'll still try to parse from the place description
        const errorMessage = geocodeError?.message || "REQUEST_DENIED";
        if (errorMessage.includes("REQUEST_DENIED") || errorMessage.includes("not authorized")) {
          setError("Google Maps API key is not authorized. Please enable Places API and Geocoding API in Google Cloud Console.");
        } else {
          setError("Geocoding service unavailable. Address fields will be filled from selected address.");
        }
        // Set results to null so we use fallback parsing
        results = null;
      }

      // Parse address components
      const address = {
        street: "",
        city: "",
        state: "",
        country: "",
        pinCode: "",
        latitude: lat,
        longitude: lng,
        fullAddress: place.description,
      };

      // Parse address components with comprehensive logic for Indian addresses
      let streetNumber = "";
      let route = "";
      const streetParts = [];
      
      // First pass: collect all address components (only if geocoding succeeded)
      if (addressComponents.length > 0) {
        addressComponents.forEach((component) => {
        const types = component.types;

        if (types.includes("street_number")) {
          streetNumber = component.long_name;
        }
        if (types.includes("route")) {
          route = component.long_name;
        }
        if (types.includes("premise")) {
          streetParts.push(component.long_name);
        }
        if (types.includes("subpremise")) {
          streetParts.push(component.long_name);
        }
        if (types.includes("administrative_area_level_1")) {
          address.state = component.long_name;
        }
        if (types.includes("country")) {
          address.country = component.long_name;
        }
        if (types.includes("postal_code")) {
          address.pinCode = component.long_name;
        }
      });
      }

      // Second pass: find city (check in priority order, but don't overwrite if already set)
      if (!address.city) {
        for (const component of addressComponents) {
          const types = component.types;
          if (types.includes("locality")) {
            address.city = component.long_name;
            break;
          }
        }
      }
      if (!address.city) {
        for (const component of addressComponents) {
          const types = component.types;
          if (types.includes("postal_town")) {
            address.city = component.long_name;
            break;
          }
        }
      }
      if (!address.city) {
        for (const component of addressComponents) {
          const types = component.types;
          if (types.includes("sublocality_level_1") || types.includes("sublocality")) {
            address.city = component.long_name;
            break;
          }
        }
      }
      if (!address.city) {
        for (const component of addressComponents) {
          const types = component.types;
          if (types.includes("administrative_area_level_2")) {
            address.city = component.long_name;
            break;
          }
        }
      }
      if (!address.city) {
        for (const component of addressComponents) {
          const types = component.types;
          if (types.includes("administrative_area_level_3")) {
            address.city = component.long_name;
            break;
          }
        }
      }

      // Combine street number and route properly
      const streetArray = [];
      if (streetNumber) streetArray.push(streetNumber);
      if (route) streetArray.push(route);
      streetArray.push(...streetParts);
      
      if (streetArray.length > 0) {
        address.street = streetArray.join(" ").trim();
      }

      // If still no street, try to extract from formatted address
      if (!address.street && results && results[0]?.formatted_address) {
        const parts = results[0].formatted_address.split(",");
        if (parts.length > 0) {
          const firstPart = parts[0].trim();
          // Use first part if it looks like a street (contains Rd, Road, Street, etc.)
          if (firstPart && (firstPart.includes("Rd") || firstPart.includes("Road") || 
              firstPart.includes("Street") || firstPart.includes("St") || 
              firstPart.includes("Nagar") || firstPart.includes("Colony") ||
              firstPart.includes("Lane") || firstPart.includes("Avenue"))) {
            address.street = firstPart;
          }
        }
      }
      
      // Fallback: use the main text from the place if street is still empty
      if (!address.street && place.structured_formatting?.main_text) {
        const mainText = place.structured_formatting.main_text;
        // Check if main text looks like a street
        if (mainText.includes("Rd") || mainText.includes("Road") || 
            mainText.includes("Street") || mainText.includes("Nagar")) {
          address.street = mainText;
        }
      }

      // Final fallback: use the place description (first part before comma)
      if (!address.street) {
        const descParts = (place.description || "").split(",");
        if (descParts.length > 0) {
          const firstPart = descParts[0].trim();
          // Use if it looks like a street
          if (firstPart && (firstPart.includes("Rd") || firstPart.includes("Road") || 
              firstPart.includes("Street") || firstPart.includes("Nagar"))) {
            address.street = firstPart;
          } else {
            address.street = firstPart; // Use anyway as last resort
          }
        }
      }

      // Clean up street address
      address.street = (address.street || "").trim();
      
      // Additional fallbacks for missing fields from formatted address
      const formattedAddress = (results && results[0]?.formatted_address) || place.description || "";
      const addressParts = formattedAddress.split(",").map(p => p.trim());

      // Parse from formatted address if components are missing
      // Format is usually: "Street, City, State, Country" or "Street, City, State, Pincode, Country"
      
      // Extract street from first part if not already set
      if (!address.street && addressParts.length > 0) {
        // First part is usually the street/route
        const firstPart = addressParts[0];
        // Check if it's not a city name (cities usually don't have "Rd", "Street", etc.)
        if (firstPart && (firstPart.includes("Rd") || firstPart.includes("Road") || 
            firstPart.includes("Street") || firstPart.includes("St") || 
            firstPart.includes("Nagar") || firstPart.includes("Colony"))) {
          address.street = firstPart;
        }
      }
      
      // Extract city - usually the part before state
      if (!address.city && addressParts.length >= 2) {
        // For "Street, City, State, Country" format
        // City is usually the second part, or the part before state
        for (let i = 1; i < addressParts.length - 1; i++) {
          const part = addressParts[i];
          // Skip if it's a number (pincode) or if it matches state
          if (part && !part.match(/^\d+$/) && part !== address.state && part !== "India") {
            address.city = part;
            break;
          }
        }
      }
      
      // Extract state - usually second to last or third to last
      if (!address.state && addressParts.length >= 2) {
        // State is usually before country
        for (let i = addressParts.length - 2; i >= 1; i--) {
          const part = addressParts[i];
          // Common Indian states
          const indianStates = [
            "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
            "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
            "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
            "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana",
            "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal"
          ];
          if (part && indianStates.some(state => part.includes(state) || state.includes(part))) {
            address.state = part;
            break;
          }
          // If no match, use the part before country as state
          if (part && part !== "India" && !part.match(/^\d+$/)) {
            address.state = part;
            break;
          }
        }
      }
      
      // Extract pincode - look for 6 digit number
      if (!address.pinCode) {
        // 1) Try any 6-digit sequence in the whole formatted string
        const pinFromFull = formattedAddress.match(/\b\d{6}\b/);
        if (pinFromFull) {
          address.pinCode = pinFromFull[0];
        } else {
          // 2) Try per-part match
          for (const part of addressParts) {
            const pinMatch = part.match(/\b\d{6}\b/); // Indian pincode is 6 digits
            if (pinMatch) {
              address.pinCode = pinMatch[0];
              break;
            }
          }
        }
      }
      
      // Extract country - usually last part
      if (!address.country && addressParts.length > 0) {
        const lastPart = addressParts[addressParts.length - 1];
        if (lastPart && !lastPart.match(/^\d+$/)) {
          address.country = lastPart;
        }
      }
      
      // Ensure country is set (default to India if in India restriction)
      if (!address.country) {
        address.country = "India";
      }
      
      // Final cleanup - remove "India" from city/state if accidentally captured
      if (address.city === "India") address.city = "";
      if (address.state === "India") address.state = "";
      
      // Final validation - ensure we have at least some data
      // If street is empty but we have the full address, use first part
      if (!address.street && place.description) {
        const descParts = place.description.split(",");
        if (descParts.length > 0) {
          address.street = descParts[0].trim();
        }
      }
      
      setSelectedPlace(place);
      setInputValue(place.description);

      // Always call the callback, even if some fields are empty
      if (onAddressSelect) {
        try {
          onAddressSelect(address);
        } catch (error) {
          console.error("Error in onAddressSelect callback:", error?.message || error);
        }
      } else {
        console.warn("onAddressSelect callback is not defined");
      }
    } catch (error) {
      console.error("Error fetching address details:", error?.message || error);
      setError("Failed to fetch address details. Please check your Google Maps API key configuration.");
      
      // Even if geocoding fails, try to parse from place description as fallback
      if (place && place.description) {
        const fallbackAddress = {
          street: place.description.split(",")[0] || "",
          city: "",
          state: "",
          country: "India",
          pinCode: "",
          latitude: null,
          longitude: null,
          fullAddress: place.description,
        };
        
        // Try to extract city and state from description
        const parts = place.description.split(",").map(p => p.trim());
        if (parts.length >= 2) {
          fallbackAddress.city = parts[1] || "";
        }
        if (parts.length >= 3) {
          fallbackAddress.state = parts[2] || "";
        }
        
        setSelectedPlace(place);
        setInputValue(place.description);
        
        if (onAddressSelect) {
          onAddressSelect(fallbackAddress);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Autocomplete
        options={status === "OK" ? data : []}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.description
        }
        loading={loading}
        freeSolo
        value={selectedPlace}
        onChange={(event, newValue) => {
          if (newValue && typeof newValue !== "string") {
            handleSelect(newValue);
          } else if (newValue === null) {
            setSelectedPlace(null);
            setInputValue("");
            setError(null);
          }
        }}
        inputValue={inputValue}
        onInputChange={(event, newInputValue, reason) => {
          setInputValue(newInputValue);
          if (ready && newInputValue && reason !== "reset" && reason !== "clear") {
            setValue(newInputValue);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            fullWidth
            disabled={false}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
              startAdornment: <LocationOn sx={{ mr: 1, color: "text.secondary" }} />,
            }}
            helperText={!ready ? "Loading Google Maps..." : ""}
          />
        )}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              py: 1.5,
            }}
          >
            <LocationOn sx={{ color: "primary.main", fontSize: 20 }} />
            <Box>
              <Box sx={{ fontWeight: 500 }}>{option.structured_formatting.main_text}</Box>
              <Box sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                {option.structured_formatting.secondary_text}
              </Box>
            </Box>
          </Box>
        )}
        filterOptions={(x) => x}
        {...props}
      />
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setError(null)} severity="warning" sx={{ width: "100%" }}>
          {error}
          <Box component="div" sx={{ mt: 1, fontSize: "0.75rem" }}>
            <strong>To fix this:</strong>
            <br />
            1. Go to{" "}
            <a
              href="https://console.cloud.google.com/apis/credentials"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "inherit", textDecoration: "underline" }}
            >
              Google Cloud Console
            </a>
            <br />
            2. Enable <strong>Places API</strong> and <strong>Geocoding API</strong>
            <br />
            3. Check API key restrictions allow these services
          </Box>
        </Alert>
      </Snackbar>
    </Box>
  );
};

const AddressAutocomplete = ({
  apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
  ...props
}) => {
  const [scriptError, setScriptError] = useState(false);

  if (!apiKey) {
    console.warn(
      "Google Maps API key is required. Please set REACT_APP_GOOGLE_MAPS_API_KEY environment variable or pass apiKey prop."
    );
    return (
      <TextField
        fullWidth
        placeholder={props.placeholder || "Search for an address"}
        helperText="Google Maps API key is required for address autocomplete"
        disabled
      />
    );
  }

  return (
    <ErrorBoundary
      fallback={
        <TextField
          fullWidth
          placeholder={props.placeholder || "Search for an address"}
          helperText="Address search unavailable. You can enter the address manually."
          value={props.value}
          onChange={props.onChange}
        />
      }
    >
      <LoadScriptNext
        googleMapsApiKey={apiKey}
        libraries={GOOGLE_MAPS_LIBRARIES}
        loadingElement={<CircularProgress size={24} />}
        onError={() => setScriptError(true)}
      >
        {scriptError && (
          <TextField
            fullWidth
            placeholder={props.placeholder || "Search for an address"}
            helperText="Error loading Google Maps. Please check your API key."
            error
            disabled
          />
        )}
        {!scriptError && <AddressAutocompleteInner {...props} />}
      </LoadScriptNext>
    </ErrorBoundary>
  );
};

export default AddressAutocomplete;

